/**
 * MiniMax 文本接口响应解析（OpenAI 兼容 /v1/chat/completions 与历史 chatcompletion_v2 并存）
 * 与前端共用：canonical 放在 server/utils，宝塔部署含 server/ 不含 src/。
 */

const trimStr = (s) => (typeof s === 'string' ? s.trim() : '');

/** 从单条 message/delta 抽取助手文本 */
export function extractMinimaxMessageBody(msg) {
  if (msg == null) return '';
  if (typeof msg === 'string') return trimStr(msg);
  if (typeof msg.content === 'string') return trimStr(msg.content);
  if (typeof msg.text === 'string') return trimStr(msg.text);
  if (Array.isArray(msg.content)) {
    const joined = msg.content
      .map((p) => {
        if (typeof p === 'string') return p;
        if (p && typeof p === 'object') {
          if (typeof p.text === 'string') return p.text;
          if (typeof p.content === 'string') return p.content;
          if (p.type === 'text' && typeof p.text === 'string') return p.text;
        }
        return '';
      })
      .join('');
    return trimStr(joined);
  }
  return '';
}

export function extractMinimaxChoiceText(choice) {
  if (!choice || typeof choice !== 'object') return '';
  if (Array.isArray(choice.messages) && choice.messages.length) {
    for (let i = choice.messages.length - 1; i >= 0; i -= 1) {
      const t = extractMinimaxMessageBody(choice.messages[i]);
      if (t) return t;
    }
  }
  const fromMsg = extractMinimaxMessageBody(choice.message);
  if (fromMsg) return fromMsg;
  const fromDelta = extractMinimaxMessageBody(choice.delta);
  if (fromDelta) return fromDelta;
  if (typeof choice.text === 'string' && choice.text.trim()) return choice.text.trim();
  if (typeof choice.content === 'string' && choice.content.trim()) return choice.content.trim();
  return '';
}

/**
 * 正文可能在 choices[].message、message、messages[]、delta、reply、result 等路径
 */
export function extractMinimaxAssistantContent(data) {
  if (!data || typeof data !== 'object') return '';

  if (Array.isArray(data.choices)) {
    for (const ch of data.choices) {
      const t = extractMinimaxChoiceText(ch);
      if (t) return t;
    }
  }

  if (typeof data.reply === 'string' && data.reply.trim()) return data.reply.trim();
  if (typeof data.text === 'string' && data.text.trim()) return data.text.trim();
  if (typeof data.output === 'string' && data.output.trim()) return data.output.trim();
  if (data.output && typeof data.output.text === 'string' && data.output.text.trim()) {
    return data.output.text.trim();
  }
  if (Array.isArray(data.outputs) && data.outputs[0] && typeof data.outputs[0].text === 'string') {
    return String(data.outputs[0].text).trim();
  }

  const nested = data.result;
  if (nested && typeof nested === 'object' && nested !== data) {
    const inner = extractMinimaxAssistantContent(nested);
    if (inner) return inner;
  }

  return '';
}

/**
 * 识别 MiniMax 业务错误（base_resp 或顶层 status_code；成功均为 0）
 * @returns {{ code: number, msg: string } | null}
 */
export function getMinimaxBusinessError(data) {
  if (!data || typeof data !== 'object') return null;
  const br = data.base_resp;
  let code = br?.status_code;
  let msg = trimStr(br?.status_msg);
  if (code === undefined || code === null) {
    code = data.status_code;
    msg = msg || trimStr(data.status_msg) || trimStr(data.message);
  }
  if (code === undefined || code === null) return null;
  const n = Number(code);
  if (Number.isNaN(n) || n === 0) return null;
  if (!msg) {
    try {
      const s = JSON.stringify(data);
      const m = /token not match group/i.exec(s);
      if (m) msg = 'token not match group';
    } catch {
      /* ignore */
    }
  }
  return { code: n, msg: msg || '接口错误' };
}

/**
 * 面向用户的 MiniMax 配置错误说明（用于抛出异常 / AI 配置测试结果）
 */
export function formatMinimaxBusinessErrorHint(err) {
  if (!err || typeof err.code !== 'number') return 'MiniMax 接口返回错误';
  const { code, msg } = err;
  const authMismatch = /token not match group/i.test(msg) || [1001, 1004].includes(code);
  const head = `MiniMax 返回错误 status_code=${code}${msg ? `（${msg}）` : ''}`;
  if (authMismatch) {
    return `${head}。常见原因：API Key 无效、过期或未授权（历史接口曾要求 URL 中 GroupId 与密钥同组；当前已改用 OpenAI 兼容地址 https://api.minimaxi.com/v1/chat/completions，仅需接口密钥中的 API Key）。请到 MiniMax 开放平台「接口密钥」核对 Key，并在本系统「系统管理 → AI 配置」中填写正确 Key 与默认端点后保存，再「测试连接」。文档：https://platform.minimaxi.com/docs/api-reference/text-chat-openai`;
  }
  return `${head}。请核对 API Key、模型名、账户余额及控制台权限。`;
}

/** 正文为空时拼可读原因（敏感词、错误码等） */
export function buildMinimaxEmptyBodyHint(data) {
  if (!data || typeof data !== 'object') return '';
  const parts = [];
  const biz = getMinimaxBusinessError(data);
  if (biz) {
    parts.push(`status_code=${biz.code}（${biz.msg}）`);
  }
  if (data.input_sensitive === true) {
    parts.push(
      `输入触发敏感校验（input_sensitive_type=${data.input_sensitive_type ?? '—'}），接口可能返回空正文`,
    );
  }
  if (data.output_sensitive === true || Number(data.output_sensitive_int) > 0) {
    parts.push(
      `输出触发敏感校验（output_sensitive_type=${data.output_sensitive_type ?? '—'}），模型未返回可见文本`,
    );
  }
  const ch0 = Array.isArray(data.choices) ? data.choices[0] : null;
  if (ch0?.finish_reason && ch0.finish_reason !== 'stop') {
    parts.push(`finish_reason=${ch0.finish_reason}`);
  }
  const joined = parts.join('；');
  if (biz && (/token not match group/i.test(biz.msg) || [1001, 1004].includes(biz.code))) {
    return formatMinimaxBusinessErrorHint(biz);
  }
  return joined;
}
